/**
 * Counter — canonical design-system numeric counter badge (src/ui/ds).
 *
 * A small rounded chip showing a count, typically used inside tabs. Four
 * sentiment tones: neutral | positive | caution | negative — semantic fills
 * (bg.*) + ink (text.body.*). Optional leading icon. The chip hugs its content
 * (min-width 20px). Theme-token driven.
 *
 * Atom: built only from primitives (Icon + Text). Composed by TabButton.
 *
 *
 * PORTED VERBATIM from ploy-client src/ui/ds/Counter.tsx — the canonical
 * design-system component. Do not restyle here; upstream owns it. Portal-local
 * changes only: dsTag inlined, viewer-only `whenToUse` export dropped.
 * Additionally: `count` accepts a string so hosts can show fractional progress
 * ("3/13") the way the access-review buckets do — numeric counts are unchanged.
 *
 * Location: src/components/ds/Counter.tsx
 */
import { Box, Icon } from "@chakra-ui/react";
import { FiUsers } from "react-icons/fi";
import type { IconType } from "react-icons";

export type CounterTone = "neutral" | "positive" | "caution" | "negative";
export type CounterSize = "sm" | "md";

export interface CounterProps {
  /** the value shown — a number, or a string for fractional progress ("3/13") */
  count: number | string;
  /** sentiment tone — drives fill (.50), ink (.700) and dot (.500) */
  tone?: CounterTone;
  /** sm = compact chip for tight hosts (small segment buttons); md = default */
  size?: CounterSize;
  /** show the leading icon (defaults to a people/users glyph) */
  showIcon?: boolean;
  /** override the leading icon */
  icon?: IconType;
  /** dim to the disabled treatment (40% opacity, preserving the tone hint) */
  isDisabled?: boolean;
}

const TONE: Record<CounterTone, { bg: string; ink: string }> = {
  // Semantic fill (bg.*) + ink (text.body.*); neutral matches the canonical in-tab
  // count badge (section fill, tertiary ink).
  neutral: { bg: "bg.section", ink: "text.body.tertiary" },
  positive: { bg: "bg.success", ink: "text.body.success" },
  caution: { bg: "bg.warning", ink: "text.body.warning" },
  negative: { bg: "bg.error", ink: "text.body.error" },
};

export function Counter({
  count, tone = "neutral", size = "md", showIcon = false, icon, isDisabled = false }: CounterProps) {
  const t = TONE[tone];
  const LeadingIcon = icon ?? FiUsers;
  const small = size === "sm";
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap="2xs"
      minW={small ? "18px" : "20px"}
      p="2xs"
      borderRadius="component.sm"
      bg={t.bg}
      color={t.ink}
      fontFamily="body"
      // header/small: semibold + xs (12px); sm drops to 2xs (10px)
      fontWeight="semibold"
      fontSize={small ? "2xs" : "xs"}
      lineHeight="1"
      opacity={isDisabled ? 0.4 : 1}
    >
      {showIcon && <Icon as={LeadingIcon} boxSize={small ? "10px" : "12px"} aria-hidden />}
      <Box as="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {count}
      </Box>
    </Box>
  );
}

export default Counter;
